import React, { useEffect, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { geoContains } from 'd3-geo';
import { Box, CircularProgress, Typography } from '@mui/material';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

/** Pastel fill / stroke so the selected country reads clearly on dark UI */
const HIGHLIGHT_FILL = '#c4e7f5';
const HIGHLIGHT_STROKE = '#5ba3c6';
const HIGHLIGHT_HOVER = '#dff2fa';
const DEFAULT_FILL = '#141416';
const DEFAULT_STROKE = '#2d2d32';

type NominatimHit = {
  lat?: string;
  lon?: string;
  address?: { country_code?: string };
};

function geoProps(geo: { properties?: Record<string, unknown> }): Record<string, unknown> {
  return (geo.properties ?? {}) as Record<string, unknown>;
}

/** Natural Earth / world-atlas can use ISO_A2, -99, or ADM0_A3 only. */
function getGeoIso2(geo: { properties?: Record<string, unknown> }): string {
  const p = geoProps(geo);
  const raw = String(p.ISO_A2 ?? p.iso_a2 ?? p.WB_A2 ?? '').toUpperCase();
  if (raw && raw !== '-99') return raw;
  const adm = String(p.ADM0_A3 ?? '').toUpperCase();
  if (adm.length === 3) {
    const fromAdm: Record<string, string> = {
      USA: 'US',
      GBR: 'GB',
      FRA: 'FR',
      NOR: 'NO',
      FIN: 'FI',
      SWE: 'SE',
      RUS: 'RU',
      CHN: 'CN',
      JPN: 'JP',
      IND: 'IN',
      BRA: 'BR',
      AUS: 'AU',
      CAN: 'CA',
      DEU: 'DE',
      ITA: 'IT',
      ESP: 'ES',
      NLD: 'NL',
      BEL: 'BE',
      POL: 'PL',
    };
    if (fromAdm[adm]) return fromAdm[adm];
  }
  return '';
}

function nameMatchesLocation(geo: { properties?: Record<string, unknown> }, location: string): boolean {
  const p = geoProps(geo);
  const candidates = [p.NAME, p.NAME_LONG, p.ADMIN, p.BRK_NAME, p.FORMAL_EN]
    .map((x) => String(x || '').trim().toLowerCase())
    .filter(Boolean);
  const q = location.trim().toLowerCase();
  if (!q || candidates.length === 0) return false;
  for (const b of candidates) {
    if (q === b) return true;
    if (q.length >= 4 && (b.includes(q) || q.includes(b))) return true;
  }
  return false;
}

function geographyContainsPoint(geo: { type?: string; geometry?: unknown; properties?: unknown }, pt: [number, number]): boolean {
  if (!Number.isFinite(pt[0]) || !Number.isFinite(pt[1])) return false;
  try {
    return Boolean(geoContains(geo as Parameters<typeof geoContains>[0], pt));
  } catch {
    return false;
  }
}

function isCountryHighlighted(
  geo: { type?: string; geometry?: unknown; properties?: Record<string, unknown> },
  iso2: string | null,
  query: string | null,
  mapPoint: [number, number] | null
): boolean {
  if (mapPoint && geographyContainsPoint(geo, mapPoint)) return true;
  const gIso = getGeoIso2(geo);
  if (iso2 && gIso && gIso === iso2) return true;
  return Boolean(query && nameMatchesLocation(geo, query));
}

/**
 * World map with the selected location’s country filled (choropleth), not a pin.
 * Uses Natural Earth geometries via world-atlas; match by Nominatim coordinates (geoContains),
 * ISO from country_code, then name fallback.
 */
export function CompanyLocationMap({ query }: { query: string | null }) {
  const [iso2, setIso2] = useState<string | null>(null);
  const [mapPoint, setMapPoint] = useState<[number, number] | null>(null);
  const [center, setCenter] = useState<[number, number]>([10, 24]);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!query?.trim()) {
      setIso2(null);
      setMapPoint(null);
      setCenter([10, 24]);
      setZoom(1);
      setErr(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setErr(null);
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query.trim()
        )}&format=json&limit=1&addressdetails=1`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Lookup failed');
        const data = (await res.json()) as NominatimHit[];
        if (cancelled) return;
        const hit = data[0];
        const cc = hit?.address?.country_code?.toUpperCase() || null;
        setIso2(cc && cc.length === 2 ? cc : null);
        const lon = hit?.lon != null ? parseFloat(String(hit.lon)) : NaN;
        const lat = hit?.lat != null ? parseFloat(String(hit.lat)) : NaN;
        if (Number.isFinite(lon) && Number.isFinite(lat)) {
          setMapPoint([lon, lat]);
          setCenter([lon, lat]);
          setZoom(2.6);
        } else {
          setMapPoint(null);
          setCenter([10, 24]);
          setZoom(1);
        }
      } catch {
        if (!cancelled) {
          setIso2(null);
          setMapPoint(null);
          setErr('Could not refine country on map; highlighting by name only.');
          setCenter([10, 24]);
          setZoom(1);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  if (!query?.trim()) {
    return (
      <Box
        sx={{
          height: 260,
          borderRadius: 2,
          border: '1px dashed #3f3f46',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#111',
        }}
      >
        <Typography variant="body2" sx={{ color: '#71717a' }}>
          Choose a location to highlight the country on the map
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box
        sx={{
          width: '100%',
          height: 280,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #27272a',
          bgcolor: '#0a0a0a',
          '& svg': { display: 'block', width: '100%', height: 'auto', maxHeight: 280 },
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(10,10,10,0.65)',
              zIndex: 2,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={36} sx={{ color: '#60a5fa' }} />
          </Box>
        )}
        <ComposableMap projection="geoMercator" width={980} height={280} projectionConfig={{ scale: 160 }}>
          <ZoomableGroup center={center} zoom={zoom}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const hi = isCountryHighlighted(geo, iso2, query, mapPoint);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={hi ? HIGHLIGHT_FILL : DEFAULT_FILL}
                      stroke={hi ? HIGHLIGHT_STROKE : DEFAULT_STROKE}
                      strokeWidth={hi ? 1 : 0.45}
                      style={{
                        default: { outline: 'none' },
                        hover: {
                          outline: 'none',
                          fill: hi ? HIGHLIGHT_HOVER : '#1e1e22',
                          stroke: hi ? HIGHLIGHT_STROKE : DEFAULT_STROKE,
                        },
                        pressed: { outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </Box>
      {err ? (
        <Typography variant="caption" sx={{ color: '#71717a', display: 'block', mt: 0.75 }}>
          {err}
        </Typography>
      ) : null}
      <Typography variant="caption" sx={{ color: '#52525b', display: 'block', mt: 0.5 }}>
        Country boundaries © Natural Earth. Location from OpenStreetMap Nominatim.
      </Typography>
    </Box>
  );
}
